import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";
import { fetchMyVenues } from "../../apis/business";
import { LoginError, syncUser } from "../../apis/auth";
import { Storage, atomWithLocalStorage } from "../../storage";

const MY_VENUES_PER_PAGE = 10_000;

/**
 * A `LoginError` is the backend refusing the session (e.g. 401 MFA_REQUIRED
 * for an unstamped portal session) — deterministic, so retrying only holds
 * the user on a blank screen for the three default attempts. Transport
 * failures keep the default three.
 */
export function shouldRetryUserSync(
  failureCount: number,
  error: unknown,
): boolean {
  return !(error instanceof LoginError) && failureCount < 3;
}

export const authAtom = atomWithQuery(() => ({
  queryKey: ["userSync"],
  retry: shouldRetryUserSync,
  queryFn: async () => {
    Storage.hydrateCurrentUserFromCookie();
    const userString = Storage.getString("currentUser");
    const user = userString ? JSON.parse(userString) : null;
    if (!user) return null;
    const response = await syncUser(user.accessToken);
    if (response.status === "success") {
      return { ...response.data, accessToken: user.accessToken };
    } else {
      Storage.clearCurrentUser();
      return null;
    }
  },
  // Force stale after 24 hours
  // staleTime: 1000 * 60 * 60 * 24,
  // staleTime: 0,  // Force immediate stale
}));

/** One shared query for all `useAuth()` consumers — avoids duplicate `fetchMyVenues` per mount. */
export const businessOwnedVenuesAtom = atomWithQuery((get) => {
  const authState = get(authAtom);
  const user = authState.data;
  const enabled =
    !!user?.accessToken &&
    (!!user?.roles?.isBusinessUser || !!user?.roles?.isBrandUser);

  return {
    queryKey: ["businessOwnedVenues", user?.accessToken],
    queryFn: async () => {
      return fetchMyVenues(user!.accessToken!, 1, MY_VENUES_PER_PAGE);
    },
    enabled,
  };
});

export const termsAndConditionsAgreedLocallyAtom =
  atomWithLocalStorage<boolean>("termsAndConditionsAgreedLocally", false);

export const remindLocationAtom = atomWithLocalStorage<boolean>(
  "remindLocation",
  false,
);

export type MfaChallengeKind = "verify" | "enrol";

/**
 * The login interrupted by the second factor, carried from the password form
 * to the code / enrolment form. In memory only: the pending token is a
 * short-lived credential and must not outlive the tab. `confirmed` parks a
 * finished enrolment's payload until the user has seen their backup codes.
 */
export interface MfaChallenge {
  kind: MfaChallengeKind;
  pendingToken: string | null;
  email: string;
  enforced: boolean;
  daysRemaining: number | null;
  /** Where to go once the session is committed. */
  from?: string;
  confirmed?: { payload: unknown; backupCodes: string[] };
}

// The cast keeps the atom writable under a project without strictNullChecks,
// where a bare `null` matches jotai's read-only overload first.
export const mfaChallengeAtom = atom<MfaChallenge | null>(
  null as MfaChallenge | null,
);
